/**
 * Widget stylesheet.
 *
 * Lives inside a shadow root, so nothing here leaks out and — more importantly —
 * the host page's own CSS cannot reach in. That matters even on sites we control:
 * a Tailwind preflight or a global `* { box-sizing }` reset would otherwise
 * reshape this UI in ways that vary per project.
 *
 * A handful of properties (font, colour, line-height, direction) still inherit
 * through the shadow boundary, so they are pinned explicitly on `.fb-root`.
 */
export const styles = String.raw`
/*
 * Custom properties live on :host. They survive the inline "all: initial" the
 * shell puts on the host element, because "all" does not reset custom properties.
 *
 * Typography deliberately does NOT live here: outer-page declarations outrank
 * :host rules, and the inline reset that defends against them would also wipe
 * anything inheritable we set here. It goes on .fb-root instead, which is inside
 * the shadow boundary and therefore unreachable from the page.
 */
:host {
  --fb-accent: #5b5bd6;
  --fb-bg: #ffffff;
  --fb-fg: #18181b;
  --fb-muted: #71717a;
  --fb-border: #e4e4e7;
  --fb-surface: #fafafa;
  --fb-danger: #dc2626;
  --fb-radius: 10px;
  --fb-shadow: 0 10px 38px rgba(0, 0, 0, 0.18), 0 3px 10px rgba(0, 0, 0, 0.10);
}

/* Everything the widget renders is inside this wrapper. */
.fb-root {
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  text-transform: none;
  color: var(--fb-fg);
  direction: ltr;
  text-align: left;
}

@media (prefers-color-scheme: dark) {
  :host {
    --fb-bg: #18181b;
    --fb-fg: #fafafa;
    --fb-muted: #a1a1aa;
    --fb-border: #3f3f46;
    --fb-surface: #27272a;
  }
}

*, *::before, *::after { box-sizing: border-box; }

button {
  font: inherit;
  color: inherit;
  cursor: pointer;
  border: 0;
  background: none;
}

/* ---------------------------------------------------------------- launcher */

.launcher {
  position: fixed;
  z-index: 2147483000;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: var(--fb-accent);
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  box-shadow: var(--fb-shadow);
  transition: transform 120ms ease, opacity 120ms ease;
}
.launcher:hover { transform: translateY(-1px); }
.launcher:active { transform: translateY(0); }
.launcher:focus-visible { outline: 2px solid var(--fb-accent); outline-offset: 3px; }
.launcher[hidden] { display: none; }

.launcher--bottom-right { bottom: 20px; right: 20px; }
.launcher--bottom-left  { bottom: 20px; left: 20px; }
.launcher--top-right    { top: 20px; right: 20px; }
.launcher--top-left     { top: 20px; left: 20px; }

/* ------------------------------------------------------------------ dialog */

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: rgba(9, 9, 11, 0.55);
  backdrop-filter: blur(2px);
}
.backdrop[hidden] { display: none; }

/*
 * The reporting frame fills the viewport. The screenshot is the thing being
 * edited, and annotating it accurately means seeing it as large as the screen
 * allows — a windowed dialog spends most of its width on the backdrop.
 */
.dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--fb-bg);
  border-radius: 0;
  box-shadow: var(--fb-shadow);
}

.dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--fb-border);
  flex: 0 0 auto;
}
.dialog__title { font-weight: 650; font-size: 15px; margin: 0; }
.dialog__subtitle { color: var(--fb-muted); font-size: 12px; margin: 2px 0 0; }

.icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: var(--fb-muted);
}
.icon-button:hover { background: var(--fb-surface); color: var(--fb-fg); }
.icon-button:focus-visible { outline: 2px solid var(--fb-accent); outline-offset: 2px; }

/*
 * The form column is capped rather than proportional: on a wide monitor a
 * fractional column grows into whitespace the screenshot could have used.
 */
.dialog__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(320px, 26vw, 400px);
  gap: 0;
  min-height: 0;
  flex: 1 1 auto;
}

/*
 * Success confirmation. The full-screen frame exists to show the screenshot;
 * once the report is filed there is nothing left to show, so the dialog
 * collapses to a small centred card. The reporting header goes with it: its
 * title and instructions no longer describe what is on screen, and
 * left-aligned chrome above a centred message reads as broken spacing.
 */
.dialog--done {
  width: min(400px, calc(100% - 48px));
  height: auto;
  max-height: calc(100% - 48px);
  border-radius: 14px;
}
.dialog--done .dialog__header { display: none; }
.dialog--done .dialog__body { grid-template-columns: minmax(0, 1fr); }

@media (max-width: 860px) {
  /* Stacked: the screenshot keeps a readable share of the height and the form
     takes the rest, scrolling within it. */
  .dialog__body {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(180px, 42%) minmax(0, 1fr);
  }
  .preview { border-right: 0; border-bottom: 1px solid var(--fb-border); }
}

/* ----------------------------------------------------------------- preview */

.preview {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--fb-surface);
  border-right: 1px solid var(--fb-border);
}

.preview__stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 14px;
}

.preview__canvas {
  max-width: 100%;
  height: auto;
  /* Centres a capture that is shorter than the stage. Auto margins, not
     align-items: center — they collapse to zero once the image overflows, so a
     tall capture stays scrollable from its top edge. */
  margin: auto;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  touch-action: none;
  cursor: crosshair;
}

.preview__status {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--fb-muted);
  font-size: 13px;
  padding: 40px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--fb-border);
  border-top-color: var(--fb-accent);
  border-radius: 50%;
  animation: fb-spin 700ms linear infinite;
  flex: 0 0 auto;
}
@keyframes fb-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .spinner { animation-duration: 2s; }
  .launcher { transition: none; }
}

/* ----------------------------------------------------------------- toolbar */

.toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--fb-border);
  flex: 0 0 auto;
  flex-wrap: wrap;
}

.tool {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 550;
  color: var(--fb-muted);
}
.tool:hover { background: var(--fb-bg); color: var(--fb-fg); }
.tool[aria-pressed="true"] { background: var(--fb-accent); color: #fff; }
.tool:focus-visible { outline: 2px solid var(--fb-accent); outline-offset: 2px; }
.tool:disabled { opacity: 0.4; cursor: not-allowed; }

.toolbar__spacer { flex: 1 1 auto; }

/* -------------------------------------------------------------------- form */

.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  overflow-y: auto;
  min-height: 0;
}

.field { display: flex; flex-direction: column; gap: 6px; }
.field__label { font-size: 12px; font-weight: 600; color: var(--fb-muted); }

.input, .textarea, .select {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  background: var(--fb-bg);
  color: var(--fb-fg);
  font: inherit;
  font-size: 13px;
}
.input:focus, .textarea:focus, .select:focus {
  outline: none;
  border-color: var(--fb-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fb-accent) 22%, transparent);
}
.textarea { resize: vertical; min-height: 92px; }

.segmented { display: flex; gap: 6px; }
.segmented__option {
  flex: 1 1 0;
  padding: 8px 6px;
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 550;
  color: var(--fb-muted);
  text-align: center;
}
.segmented__option[aria-pressed="true"] {
  border-color: var(--fb-accent);
  background: color-mix(in srgb, var(--fb-accent) 12%, transparent);
  color: var(--fb-accent);
}

.row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

.form__footer {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
}
.button--primary { background: var(--fb-accent); color: #fff; }
.button--primary:hover { filter: brightness(1.07); }
.button--primary:disabled { opacity: 0.55; cursor: not-allowed; filter: none; }
.button--ghost { color: var(--fb-muted); }
.button--ghost:hover { background: var(--fb-surface); color: var(--fb-fg); }
.button:focus-visible { outline: 2px solid var(--fb-accent); outline-offset: 2px; }

/* ------------------------------------------------------------------ notices */

.notice {
  padding: 9px 11px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.45;
}
.notice--warn {
  background: color-mix(in srgb, #f59e0b 14%, transparent);
  color: #92400e;
}
.notice--error {
  background: color-mix(in srgb, var(--fb-danger) 12%, transparent);
  color: var(--fb-danger);
}
@media (prefers-color-scheme: dark) {
  .notice--warn { color: #fcd34d; }
}

.meta-summary {
  font-size: 11px;
  color: var(--fb-muted);
  border-top: 1px solid var(--fb-border);
  padding-top: 10px;
  line-height: 1.6;
}
.meta-summary b { color: var(--fb-fg); font-weight: 600; }

/* ----------------------------------------------------------------- success */

.success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 28px 28px;
  text-align: center;
}
.success p { margin: 0; color: var(--fb-muted); }
.success .button { width: 100%; margin-top: 6px; }
.success__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: color-mix(in srgb, #16a34a 16%, transparent);
  color: #16a34a;
}
.success__title { font-size: 16px; font-weight: 650; margin: 0; }
.success__link { color: var(--fb-accent); font-weight: 600; text-decoration: none; }
.success__link:hover { text-decoration: underline; }

.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
`;
